import { Button, TextField } from "@clockwise/design-system";
import { Search } from "@clockwise/design-system/icons";
import { CircularProgress, MenuItem, Paper, Popper } from "@material-ui/core";
import React from "react";
import Autosuggest, {
  RenderSuggestionParams,
  RenderSuggestionsContainerParams,
  SuggestionSelectedEventData,
} from "react-autosuggest";
import PlacesAutocomplete, { Suggestion as IAddressSuggestion } from "react-places-autocomplete";

export type AddressSearchMode = "standard" | "onboarding";

export interface IContainer {
  disabled?: boolean;
  label: string;
  loadingIcon?: React.ReactElement;
  onCancel: () => void;
  onSelectAddress: (address: string) => void;
}

export interface IProps extends IContainer {
  mode: AddressSearchMode;
  initialQuery: string;
}

export interface IState {
  query: string;
  anchorEl: HTMLElement | null;
  googleLoaded: boolean;
  focused: boolean;
}

export class AddressSearch extends React.Component<IProps, IState> {
  static defaultProps = {
    mode: "standard" as AddressSearchMode,
    initialQuery: "",
  };

  private googleInterval?: ReturnType<typeof setInterval>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      query: props.initialQuery,
      anchorEl: null,
      googleLoaded: false,
      focused: false,
    };
  }

  public componentDidMount() {
    const googleLoaded = (window as any).google && (window as any).google.maps;
    if (googleLoaded) {
      this.setState({ googleLoaded });
    } else {
      this.googleInterval = setInterval(() => {
        const googleLoaded = (window as any).google && (window as any).google.maps;
        if (googleLoaded) {
          this.setState({ googleLoaded });
          this.googleInterval && clearInterval(this.googleInterval);
          delete this.googleInterval;
        }
      }, 5000);
    }
  }

  public componentWillUnmount() {
    if (this.googleInterval) {
      clearInterval(this.googleInterval);
    }
  }

  // ~-~-~-~-~-~-~-
  // Autosuggest Helpers
  // ~-~-~-~-~-~-~-
  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  private getSuggestionValue = (suggestion: IAddressSuggestion) => {
    return suggestion.description;
  };

  private onSuggestionSelected = (
    _event: React.FormEvent<HTMLDivElement>,
    data: SuggestionSelectedEventData<IAddressSuggestion>,
  ) => {
    this.props.onSelectAddress(data.suggestion.description);
    this.setState({ query: data.suggestion.description });
  };

  private onInputFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.setState({ anchorEl: e.currentTarget, focused: true });
  };

  private onInputBlur = () => {
    this.setState({ focused: false });
  };

  private onInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const key = e.key || e.keyCode;
    switch (key) {
      case "Escape":
      case 27:
        e.preventDefault();
        e.stopPropagation();
      default:
        break;
    }
  };

  private onChange = (event: React.ChangeEvent<HTMLInputElement>, inputPropsOnChange: Function) => {
    const query = event.target.value;
    if (this.state.query === query) {
      return;
    }
    this.setState({ query });
    inputPropsOnChange(event);
  };

  // ~-~-~-~-~-~-~-
  // Other Helpers
  // ~-~-~-~-~-~-~-
  private onCancel = () => {
    this.props.onCancel();
    this.setState({ query: "" });
  };

  private focusInput = (input: any) => {
    if (input && input.focus) {
      setTimeout(() => {
        input.focus();
      }, 100);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderSuggestionsContainer = (params: RenderSuggestionsContainerParams) => {
    const { anchorEl } = this.state;
    return (
      <Popper
        anchorEl={anchorEl}
        open={!!params.children}
        placement="bottom-start"
        style={{ zIndex: 1301 }}
      >
        <Paper {...params.containerProps} square style={{ width: 400 }}>
          {params.children}
        </Paper>
      </Popper>
    );
  };

  private renderSuggestion = (suggestion: IAddressSuggestion, params: RenderSuggestionParams) => {
    return (
      <MenuItem
        key={suggestion.description}
        classes={{
          root: params.isHighlighted ? "cw-bg-grey-lightest" : undefined,
        }}
      >
        {suggestion.description}
      </MenuItem>
    );
  };

  private renderInputComponent: Autosuggest.RenderInputComponent<IAddressSuggestion> = (
    inputProps,
  ) => {
    const { mode } = this.props;

    return (
      <div className="cw-w-full">
        <div className="cw-flex cw-justify-between cw-items-center cw-w-full cw-gap-3">
          {/* TODO (lsanwick) resolve the type differences between the Autocomplete input props and TextField */}
          <TextField startIcon={Search} {...(inputProps as any)} />
          {mode === "standard" && (
            <Button
              onClick={this.onCancel}
              variant="outlined"
              aria-label="Cancel inputting address"
              disabled={this.props.disabled}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  };

  public render() {
    if (!this.state.googleLoaded) {
      return this.props.loadingIcon || <CircularProgress />;
    }

    const { mode } = this.props;

    return (
      <div className={`${this.state.focused ? " active" : ""}`}>
        <PlacesAutocomplete
          value={this.state.query}
          onSelect={() => null}
          onChange={() => null}
          searchOptions={{
            types: ["address"],
          }}
        >
          {({ getInputProps, suggestions }) => {
            const placesAutocompleteProps = getInputProps();
            return (
              <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={() => null}
                onSuggestionsClearRequested={() => null}
                renderSuggestionsContainer={this.renderSuggestionsContainer}
                renderSuggestion={this.renderSuggestion}
                renderInputComponent={this.renderInputComponent}
                getSuggestionValue={this.getSuggestionValue}
                onSuggestionSelected={this.onSuggestionSelected}
                highlightFirstSuggestion
                inputProps={{
                  placeholder: this.props.label,
                  value: this.state.query,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    this.onChange(e, placesAutocompleteProps.onChange),
                  autoComplete: "off",
                  ref: this.focusInput,
                  onFocus: this.onInputFocus,
                  onBlur: this.onInputBlur,
                  onKeyDown: this.onInputKeyDown,
                  disableunderline: String(mode === "onboarding"),
                  disabled: this.props.disabled,
                }}
                theme={{
                  container: "cw-position-relative",
                  suggestionsList: "cw-m-0 cw-p-0 cw-list-none",
                  suggestion: "cw-display-block",
                }}
              />
            );
          }}
        </PlacesAutocomplete>
      </div>
    );
  }
}
